import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from '@tanstack/react-router'
import { ApiError } from '@/shared/api/client'
import { useAppStore } from '@/shared/stores/appStore'
import { createLeague, leagueKeys } from '../queries'
import { useCompetitions } from './useCompetitions'
import {
  LeagueScoringSystem,
  LeagueFormat,
  LeagueTransferOption,
  type LeagueFormValues,
} from '../types'

const leagueFormSchema = z.object({
  name: z
    .string()
    .min(3, 'Nome deve ter no mínimo 3 caracteres.')
    .max(40, 'Máximo 40 caracteres.'),
  teamName: z
    .string()
    .max(30, 'Máximo 30 caracteres.')
    .refine((v) => v.trim().length === 0 || v.trim().length >= 3, {
      message: 'O nome do time deve ter pelo menos 3 caracteres.',
    }),
  competitionId:  z.string().min(1, 'Selecione uma competição.'),
  scoringSystem:  z.enum(['NOSSA_PRESET']),
  format:         z.enum(['OVERALL_POINTS', 'HEAD_TO_HEAD']),
  transferOption: z.enum(['OPTION_A', 'OPTION_B']),
})

export function useLeagueCreation() {
  const [formError, setFormError] = useState<string | null>(null)
  const navigate          = useNavigate()
  const addToast          = useAppStore((s) => s.addToast)
  const setActiveLeagueId = useAppStore((s) => s.setActiveLeagueId)
  const queryClient       = useQueryClient()
  const { active: activeCompetition } = useCompetitions()

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<LeagueFormValues>({
    resolver: zodResolver(leagueFormSchema),
    mode: 'onBlur',
    defaultValues: {
      name:           '',
      teamName:       '',
      competitionId:  '',
      scoringSystem:  LeagueScoringSystem.NossaPreset,
      format:         LeagueFormat.OverallPoints,
      transferOption: LeagueTransferOption.OptionB,
    },
  })

  // Auto-select the active competition once it loads
  useEffect(() => {
    if (activeCompetition && !watch('competitionId')) {
      setValue('competitionId', activeCompetition.id, { shouldValidate: false })
    }
  }, [activeCompetition, setValue, watch])

  const mutation = useMutation({
    mutationFn: (data: LeagueFormValues) =>
      createLeague({
        name:           data.name,
        competitionId:  data.competitionId,
        format:         data.format,
        transferOption: data.transferOption,
        teamName:       data.teamName.trim() || undefined,
      }),
    onSuccess: (result) => {
      setActiveLeagueId(result.leagueId)
      queryClient.setQueryData(leagueKeys.detail(result.leagueId), result)
      void queryClient.invalidateQueries({ queryKey: leagueKeys.list() })
      addToast({
        type:       'success',
        title:      'Liga criada!',
        body:       'Convide seus amigos com o código da liga.',
        durationMs: 6000,
      })
      void navigate({
        to:     '/league/$leagueId',
        params: { leagueId: result.leagueId },
        search: { tab: 'tabela', gw: undefined },
      })
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError) {
        if (error.errorCode === 'league_limit_reached') {
          setFormError(
            'Você atingiu o limite de ligas do seu plano. Faça upgrade para criar mais ligas.',
          )
          return
        }
        addToast({
          type:  'error',
          title: 'Não foi possível criar a liga. Tente novamente.',
        })
        return
      }
      throw error
    },
  })

  function onSubmit(data: LeagueFormValues) {
    setFormError(null)
    mutation.mutate(data)
  }

  return {
    register,
    control,
    handleSubmit,
    watch,
    trigger,
    errors,
    formError,
    isCreating: mutation.isPending,
    onSubmit,
  }
}
